import { useEffect } from "react";
import { Link } from "react-router-dom";
import type { VideoItem } from "@/types/content";

interface VideoModalProps {
  video: VideoItem | null;
  onClose: () => void;
}

export default function VideoModal({ video, onClose }: VideoModalProps) {
  useEffect(() => {
    if (!video) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [video, onClose]);

  if (!video) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/85 p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl rounded-lg overflow-hidden bg-background-50 animate-zoom-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-background-200">
          <p className="text-sm md:text-base font-medium text-foreground-950 truncate pr-4">{video.title}</p>
          <button
            type="button"
            aria-label="关闭播放器"
            onClick={onClose}
            className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="relative w-full aspect-video bg-foreground-950">
          <img
            src={video.cover}
            alt={video.title}
            title={`${video.title} 韩国女主播视频`}
            className="w-full h-full object-cover object-top opacity-60"
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <button
              type="button"
              aria-label="播放"
              className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer"
            >
              <i className="ri-play-fill text-3xl"></i>
            </button>
            <p className="mt-4 text-background-50 font-heading font-semibold">{video.streamerName} · {video.type}</p>
            <p className="mt-2 text-xs md:text-sm text-background-200/80 max-w-md leading-relaxed">
              本播放器为前端演示效果，暂未接入真实视频播放与下载数据。
            </p>
          </div>
          <span className="absolute bottom-3 right-3 px-2 py-0.5 rounded text-xs bg-foreground-950/75 text-background-50">
            {video.duration}
          </span>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-4 md:px-5 py-4">
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span>
              <i className="ri-eye-line mr-1"></i>
              {video.views} 次播放
            </span>
            <span>
              <i className="ri-calendar-line mr-1"></i>
              {video.date}
            </span>
          </div>
          <Link
            to={`/detail/${video.streamerId}`}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            查看主播主页
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </div>
  );
}